import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../utils/axios';
import { useFormValidation } from '../../utils/validation';
import './auth.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const navigate = useNavigate();

  const {
    fieldErrors,
    fieldValid,
    touched,
    handleBlur,
    handleFieldChange,
    validateAllFields,
    getFieldClass
  } = useFormValidation(['email', 'password', 'confirmPassword']);

  useEffect(() => {
    const resetTokenParam = searchParams.get('token');
    if (resetTokenParam) {
      setResetToken(resetTokenParam);
    } else {
      setError('No reset token provided');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form before submission
    const formData = { email, password, confirmPassword };
    if (!validateAllFields(formData)) {
      setLoading(false);
      setError('Please fix the errors above before submitting');
      return;
    }

    try {
      const response = await apiClient.post('/iam/reset-password', {
        reset_token: resetToken,
        email: email.trim().toLowerCase(),
        password: password
      });

      const data = response.data;

      if (data.success) {
        // Navigate to signup page with invitation data in state
        navigate('/login', {
          state: {
            email: email.trim().toLowerCase(),
          }
        });
      } else {
        setError(data.message || 'Reset password failed');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'Validation failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!resetToken) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invalid Reset Token</h1>
          </div>
          <div className="error-message">
            No reset token provided. Please check your reset token and try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Reset Password</h1>
          <p>Please enter your new password</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className={`form-group ${getFieldClass('email')}`}>
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                handleFieldChange('email', e.target.value, { email: e.target.value, password, confirmPassword });
              }}
              onBlur={(e) => handleBlur('email', e.target.value, { email: e.target.value, password, confirmPassword })}
              placeholder="Enter your email address"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
              {fieldValid.email && !fieldErrors.email && (
                <span className="field-success">✓ Valid email address</span>
              )}
            </div>
          </div>

          <div className={`form-group ${getFieldClass('password')}`}>
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => {
                const newPassword = e.target.value;
                setPassword(newPassword);
                const formData = { email, password: newPassword, confirmPassword };
                handleFieldChange('password', newPassword, formData);
                // Also validate confirmPassword if it was touched
                if (touched.confirmPassword) {
                  handleFieldChange('confirmPassword', confirmPassword, formData);
                }
              }}
              onBlur={(e) => handleBlur('password', e.target.value, { email, password: e.target.value, confirmPassword })}
              placeholder="Enter your new password"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.password && (
                <span className="field-error">{fieldErrors.password}</span>
              )}
              {fieldValid.password && !fieldErrors.password && (
                <span className="field-success">✓ Strong password</span>
              )}
              {!touched.password && !fieldErrors.password && !fieldValid.password && (
                <small className="form-hint">Must include uppercase, lowercase, number, and special character</small>
              )}
            </div>
          </div>

          <div className={`form-group ${getFieldClass('confirmPassword')}`}>
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                handleFieldChange('confirmPassword', e.target.value, { email, password, confirmPassword: e.target.value });
              }}
              onBlur={(e) => handleBlur('confirmPassword', e.target.value, { email, password, confirmPassword: e.target.value })}
              placeholder="Confirm your new password"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.confirmPassword && (
                <span className="field-error">{fieldErrors.confirmPassword}</span>
              )}
              {fieldValid.confirmPassword && !fieldErrors.confirmPassword && (
                <span className="field-success">✓ Passwords match</span>
              )}
            </div>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Having trouble? Contact your administrator for assistance. You can use the same email address to sign in after resetting your password.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 